import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Drawer,
  Typography,
  List,
  ListItem,
  IconButton,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  Pagination,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import {
  History as HistoryIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Restore as RestoreIcon,
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  TextFields as TextIcon,
  Translate as TranslateIcon,
} from "@mui/icons-material";
import { 
  getTranslationHistory, 
  deleteTranslation, 
  updateTranslation 
} from "../../services/translatorHistoryService";

const TranslatorHistory = ({ open, onClose, onRestoreTranslation, userId, machineId }) => {
  const [translations, setTranslations] = useState([]);
  const [filteredTranslations, setFilteredTranslations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTranslation, setSelectedTranslation] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editText, setEditText] = useState("");
  const [editId, setEditId] = useState(null);

  const itemsPerPage = 10;

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open, userId, machineId]);

  useEffect(() => {
    filterTranslations();
  }, [searchTerm, translations]);

  const filterTranslations = () => {
    let filtered = [...translations];
    
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.originalText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.translatedText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.targetLanguage?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredTranslations(filtered);
    setPage(1);
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await getTranslationHistory(userId, machineId);
      if (response.success) {
        setTranslations(response.data);
        setFilteredTranslations(response.data);
      }
    } catch (error) {
      showSnackbar("Failed to load translation history", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (translation) => {
    onRestoreTranslation(translation.translatedText);
    showSnackbar("Translation restored successfully", "success");
    onClose();
  };

  const handleDelete = async () => {
    if (!selectedTranslation) return;
    
    try {
      await deleteTranslation(selectedTranslation._id);
      showSnackbar("Translation deleted successfully", "success");
      fetchHistory();
      setDeleteDialogOpen(false);
      setSelectedTranslation(null);
    } catch (error) {
      showSnackbar("Failed to delete translation", "error");
    }
  };

  const handleEdit = (translation) => {
    setEditId(translation._id);
    setEditText(translation.translatedText);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editId || !editText.trim()) return;
    
    try {
      await updateTranslation(editId, editText);
      showSnackbar("Translation updated successfully", "success");
      fetchHistory();
      setEditDialogOpen(false);
      setEditText("");
      setEditId(null);
    } catch (error) {
      showSnackbar("Failed to update translation", "error");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const totalPages = Math.ceil(filteredTranslations.length / itemsPerPage);
  const paginatedTranslations = filteredTranslations.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 550, md: 650 },
            bgcolor: "background.paper",
            borderRadius: { sm: "16px 0 0 16px" },
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "#667eea",
              color: "white",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TranslateIcon />
              <Typography variant="h6">Translation History</Typography>
              <Chip
                label={`${translations.length} items`}
                size="small"
                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
              />
            </Box>
            <IconButton onClick={onClose} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search Bar */}
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by content or language..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* List of Translations */}
          <Box sx={{ flex: 1, overflow: "auto", px: 2 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : paginatedTranslations.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <TranslateIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography color="text.secondary">No saved translations yet</Typography>
                <Typography variant="body2" color="text.secondary">
                  Your translated content will appear here
                </Typography>
              </Box>
            ) : (
              <List>
                {paginatedTranslations.map((item, index) => (
                  <React.Fragment key={item._id}>
                    {index > 0 && <Divider />}
                    <ListItem
                      sx={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        py: 2,
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <Box sx={{ width: "100%", mb: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                          <Box sx={{ flex: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              {/* Language Badge */}
                              <Chip
                                icon={<TranslateIcon />}
                                label={`${item.sourceLanguage || "Auto"} → ${item.targetLanguage}`}
                                size="small"
                                sx={{
                                  bgcolor: "#667eea",
                                  color: "white",
                                  fontWeight: 600,
                                }}
                              />
                              
                              {/* Word Count */}
                              <Chip
                                icon={<TextIcon />}
                                label={`${item.translatedWordCount || item.translatedText?.split(/\s+/).length || 0} words`}
                                size="small"
                                variant="outlined"
                              />
                              
                              {/* Date */}
                              <Chip
                                icon={<CalendarIcon />}
                                label={formatDate(item.createdAt)}
                                size="small"
                                variant="outlined"
                              />
                            </Stack>
                          </Box>
                          
                          {/* Action Buttons */}
                          <Box>
                            <Tooltip title="Restore">
                              <IconButton
                                size="small"
                                onClick={() => handleRestore(item)}
                                sx={{ color: "success.main" }}
                              >
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(item)}
                                sx={{ color: "info.main" }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedTranslation(item);
                                  setDeleteDialogOpen(true);
                                }}
                                sx={{ color: "error.main" }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        {/* Original Text Preview */}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          Original:
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            mb: 1,
                            pl: 1,
                            borderLeft: 2,
                            borderColor: "primary.light",
                          }}
                        >
                          {item.originalText?.substring(0, 150)}
                          {item.originalText?.length > 150 && "..."}
                        </Typography>

                        {/* Translated Text Preview */}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          Translated ({item.targetLanguage}):
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            pl: 1,
                            borderLeft: 2,
                            borderColor: "#667eea",
                            fontWeight: 500,
                          }}
                        >
                          {item.translatedText?.substring(0, 200)}
                          {item.translatedText?.length > 200 && "..."}
                        </Typography>
                      </Box>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {totalPages > 1 && (
            <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                size="small"
                sx={{ display: "flex", justifyContent: "center" }}
              />
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Translated Text</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Translated Text"
            type="text"
            fullWidth
            multiline
            rows={10}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Translation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this translation?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TranslatorHistory;